import CloseIcon from '@mui/icons-material/Close';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import SearchIcon from '@mui/icons-material/Search';
import Button from '@mui/material/Button';
import InputAdornment from '@mui/material/InputAdornment';
import React, { useEffect, useMemo, useState } from 'react';
import Modal from 'react-modal';

import { Loading } from '@brands/Components/LoadingSpinner/Loading';
import CustomTextField from '@brands/Components/Inputs/TextField/TextField';
import { customStylesDashboard } from '@brands/Utils/customStyles';
import styles from './styles.module.scss';
import TextField from '@brands/Components/Inputs/MuiTextField/TextField';

export type SmallSelectionOption = {
  value: string;
  label: string;
};

type SmallSelectionModalProps = {
  open: boolean;
  title: string;
  description?: string;
  options: SmallSelectionOption[];
  selectedValue: string;
  onChange: (value: string) => void;
  primaryLabel?: string;
  onPrimaryClick: () => Promise<void> | void;
  secondaryLabel?: string;
  onSecondaryClick?: () => void;
  onClose: () => void;
  loading?: boolean;
  loadError?: string | null;
  onRetry?: () => Promise<void>;
  searchable?: boolean;
  searchPlaceholder?: string;
  // When the user picks `extraTextValue` (e.g. 'OTHER'), the modal
  // reveals a free-text field gated by `extraTextLabel`. The primary
  // button is disabled until that text is non-empty. Used by the
  // cancel-case flow to collect a free-form reason for "Other" so
  // the backend's "Please include a message for 'Other'" guard
  // (cases/cancel_case.py) is satisfied.
  extraTextValue?: string;
  extraTextLabel?: string;
  extraTextPlaceholder?: string;
  extraText?: string;
  onExtraTextChange?: (value: string) => void;
};

const modalStyles = {
  ...customStylesDashboard,
  content: {
    ...customStylesDashboard.content,
    width: '420px',
    maxWidth: '95vw',
    padding: '0',
    borderRadius: '8px',
    overflow: 'hidden',
    boxShadow: '0 8px 10px rgba(0,0,0,0.20), 0 16px 24px rgba(0,0,0,0.14), 0 6px 30px rgba(0,0,0,0.12)',
  },
};

const SmallSelectionModal = ({
  open,
  title,
  description,
  options,
  selectedValue,
  onChange,
  primaryLabel = 'Apply',
  onPrimaryClick,
  secondaryLabel,
  onSecondaryClick,
  onClose,
  loading = false,
  loadError,
  onRetry,
  searchable = false,
  searchPlaceholder = 'Search by name',
  extraTextValue,
  extraTextLabel,
  extraTextPlaceholder,
  extraText = '',
  onExtraTextChange,
}: SmallSelectionModalProps): JSX.Element => {
  const [searchValue, setSearchValue] = useState('');

  const showExtraText = Boolean(extraTextValue) && selectedValue === extraTextValue;
  const extraTextRequired = showExtraText && Boolean(onExtraTextChange);
  const extraTextSatisfied = !extraTextRequired || extraText.trim().length > 0;

  useEffect(() => {
    if (open) {
      setSearchValue('');
    }
  }, [open]);

  const filteredOptions = useMemo(() => {
    if (!searchable || !searchValue.trim()) return options;

    const normalized = searchValue.trim().toLowerCase();

    return options.filter((option) => option.label.toLowerCase().includes(normalized));
  }, [options, searchable, searchValue]);

  const handlePrimaryClick = async (): Promise<void> => {
    if (!selectedValue || loading || loadError || !extraTextSatisfied) return;
    await onPrimaryClick();
  };

  const showSearch = searchable && !loading && !loadError && options.length > 0;
  const showEmptyState = !loading && filteredOptions.length === 0;

  return (
    <Modal isOpen={open} onRequestClose={onClose} ariaHideApp={false} style={modalStyles}>
      <div className={styles.smallSelectionModal}>
        <div className={styles.header}>
          <span className={styles.title}>{title}</span>
          <CloseIcon className={styles.closeIcon} onClick={onClose} />
        </div>

        {description && <p style={{ padding: '0 24px' }}>{description}</p>}

        {showSearch && (
          <div className={styles.searchWrap}>
            <TextField
              label="Search by name"
              data-testid="search-by-name"
              name="smallSelectionSearch"
              value={searchValue}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchValue(e.target.value)}
              wrapperClassName={styles.searchFieldWrapper}
              inputClass={styles.searchFieldInput}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon className={styles.searchIcon} />
                  </InputAdornment>
                ),
              }}
            />
          </div>
        )}

        <div className={`${styles.body} ${showSearch ? styles.limitedHeight : ''}`}>
          {loading ? (
            <div className={styles.loadingContainer}>
              <Loading dotted />
            </div>
          ) : loadError ? (
            <div className={styles.emptyState} role="alert">
              <p>{loadError}</p>
              {onRetry && <Button onClick={onRetry}>Retry</Button>}
            </div>
          ) : showEmptyState ? (
            <div className={styles.emptyState}>
              {searchValue.trim() ? 'No matching results found.' : 'No options available.'}
            </div>
          ) : (
            <>
              {filteredOptions.map((option) => {
                const isSelected = selectedValue === option.value;

                return (
                  <button
                    key={option.value}
                    type="button"
                    className={styles.optionRow}
                    onClick={() => onChange(option.value)}
                    disabled={loading}
                  >
                    <span className={styles.optionLabel}>{option.label}</span>
                    {isSelected ? (
                      <RadioButtonCheckedIcon className={styles.radioIconSelected} />
                    ) : (
                      <RadioButtonUncheckedIcon className={styles.radioIcon} />
                    )}
                  </button>
                );
              })}

              {showExtraText && onExtraTextChange && (
                <div className={styles.extraTextWrap}>
                  <TextField
                    name="smallSelectionExtraText"
                    label={extraTextLabel || 'Please specify'}
                    value={extraText}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => onExtraTextChange(e.target.value)}
                    placeholder={extraTextPlaceholder}
                    multiline
                    minRows={4}
                    errors={[]}
                    wrapperClassName={styles.extraTextField}
                  />
                </div>
              )}
            </>
          )}
        </div>

        <div className={styles.footer}>
          <Button
            variant="contained"
            className={styles.applyBtn}
            onClick={handlePrimaryClick}
            disabled={!selectedValue || loading || Boolean(loadError) || !extraTextSatisfied}
          >
            {primaryLabel}
          </Button>

          {secondaryLabel && onSecondaryClick && (
            <Button variant="text" className={styles.secondaryBtn} onClick={onSecondaryClick} disabled={loading}>
              {secondaryLabel}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default SmallSelectionModal;
